import {
  IonAlert,
  IonButton,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonInput,
  IonRow,
  IonSpinner,
  useIonViewWillLeave,
} from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import Select from "react-select";
import areas from "../assets/json/AllAreas.json";
import { APIService } from "../network/APIService";
import AppContext from "../providers/AppContext";
import { Group } from "../utils/Interfaces";
import "./EditUserGrid.css";

const EditUserGrid = ({ forNewUser }: { forNewUser: boolean }) => {
  const appContext = useContext(AppContext);

  const [groupChoices, setGroupChoices] = useState<string[]>([]);
  const [loadingGroups, setLoadingGroups] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [savingChanges, setSavingChanges] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const [forename, setForename] = useState("");
  const [surname, setSurname] = useState("");

  useEffect(() => {
    getGroupChoices();
  }, []);

  useIonViewWillLeave(() => {
    setForename(prev => "");
    setSurname("");
  });

  const getGroupChoices = async () => {
    setLoadingGroups(true);
    const groups = await APIService.getGroups(appContext.jwt);
    if (groups) {
      // successfully got groups
      let newList: string[] = [];
      groups.forEach((grp: Group) => newList.push(grp.name));
      setErrorMessage("");
      setGroupChoices(newList);
    } else {
      // failed to get groups
      setErrorMessage("Failed to get groups");
    }
    setLoadingGroups(false);
  };

  let areaOptions: { value: string; label: string }[] = [];
  areas.forEach(area => {
    areaOptions.push({
      value: area,
      label: area,
    });
  });

  let title = "";
  if (forNewUser) {
    title = "New User";
  } else {
    title = appContext.userToEdit.displayName;
  }

  const shouldDisableButtton = () => {
    const user = appContext.userToEdit!;
    const emptyEmail = user.email.trim() === "";
    const emptyArea = user.area.trim() === "";
    const noGroupsSelected = user.groups === "";
    let emptyName: boolean = false;
    if (forNewUser) {
      emptyName = forename.trim() === "" || surname.trim() === "";
    } else {
      emptyName = user.displayName.trim() === "";
    }

    return emptyName || emptyEmail || emptyArea || noGroupsSelected;
  };

  const handleSaveChanges = async () => {
    setSavingChanges(true);
    let success: boolean = false;
    if (forNewUser) {
      success = await APIService.createUser(
        appContext.jwt,
        appContext.userToEdit.email,
        appContext.userToEdit.groups,
        surname,
        forename,
        appContext.userToEdit.area,
        appContext.userToEdit.weeklyReports
      );
    } else {
      success = await APIService.editUser(
        appContext.jwt,
        appContext.userToEdit.id,
        appContext.userToEdit.groups,
        appContext.userToEdit.displayName,
        appContext.userToEdit.area,
        appContext.userToEdit.weeklyReports
      );
    }
    if (success) {
      setAlertMessage("Changes saved!");
    } else {
      setAlertMessage(
        forNewUser
          ? "Failed to create a new user. Please try again later."
          : "Failed to save changes. Please try again later."
      );
    }
    // update current user object if the user was editing themself
    if (success && appContext.currentUser?.email === appContext.userToEdit.email) {
      appContext.setCurrentUser({
        ...appContext.currentUser,
        displayName: appContext.userToEdit.displayName,
        area: appContext.userToEdit.area,
        groups: appContext.userToEdit.groups,
        weeklyReports: appContext.userToEdit.weeklyReports,
      });
    }
    setShowAlert(true);
    setSavingChanges(false);
  };

  return (
    <div>
      <h1 className="pageHeader">{title}</h1>
      <IonGrid>
        <IonRow>
          <IonCol>
            {forNewUser ? (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ flex: 1, marginRight: "8px" }}>
                  <p className="editUserInputLabel">Forename</p>
                  <IonInput
                    value={forename}
                    className="inputMargins"
                    onIonChange={e => setForename(e.detail.value!)}
                    type="text"
                  />
                </div>
                <div style={{ flex: 1, marginLeft: "8px" }}>
                  <p className="editUserInputLabel">Surname</p>
                  <IonInput
                    value={surname}
                    className="inputMargins"
                    onIonChange={e => setSurname(e.detail.value!)}
                    type="text"
                  />
                </div>
              </div>
            ) : (
              <>
                <p className="editUserInputLabel">Display Name</p>
                <IonInput
                  className="inputMargins"
                  value={appContext.userToEdit?.displayName}
                  onIonChange={e =>
                    appContext.setUserToEdit({ ...appContext.userToEdit!, displayName: e.detail.value! })
                  }
                  type="text"
                />
              </>
            )}
            <p className="editUserInputLabel">Email</p>
            <IonInput
              onIonChange={e => appContext.setUserToEdit({ ...appContext.userToEdit!, email: e.detail.value! })}
              className="inputMargins"
              disabled={!forNewUser}
              value={appContext.userToEdit?.email}
              type="email"
            />
            <p className="editUserInputLabel">Local Authority or Police Area</p>
            <Select
              styles={{
                //   option: (provided, state) => ({ ...provided }),
                control: (provided:any) => ({
                  ...provided,
                  borderRadius: 8,
                  border: "solid 1px var(--input-border) !important",
                  height: 43,
                  marginTop: 8,
                  boxShadow: "none",
                }),
              }}
              className="areaInput"
              options={areaOptions}
              value={{
                value: appContext.userToEdit?.area,
                label: appContext.userToEdit?.area,
              }}
              onChange={(newValue:any) =>
                appContext.setUserToEdit({ ...appContext.userToEdit!, area: newValue?.value ? newValue?.value : "" })
              }
            />
            <p className="editUserInputLabel">Groups</p>
            {loadingGroups ? (
              <IonSpinner style={{ marginTop: "12px" }} />
            ) : errorMessage ? (
              <p className="modalErrMessage">{errorMessage}</p>
            ) : (
              groupChoices.map((choice, index) => {
                const active = appContext.userToEdit?.groups ? appContext.userToEdit.groups.includes(choice) : false;
                return (
                  <div
                    key={index}
                    onClick={() => {
                      let newArr: string[] = [];
                      if (appContext.userToEdit!.groups !== "") {
                        newArr = appContext.userToEdit!.groups.split(",").slice();
                      }
                      const index = newArr.indexOf(choice);
                      index === -1 ? newArr.push(choice) : newArr.splice(index, 1);
                      appContext.setUserToEdit({ ...appContext.userToEdit!, groups: newArr.toString() });
                    }}
                    className="groupBubbleContainer"
                    style={{ color: active ? "white" : "black", backgroundColor: active ? "var(--p-30)" : "white" }}
                  >
                    <div className="groupBubble">{choice}</div>
                  </div>
                );
              })
            )}
            <p id="groupsText">Click on groups to toggle on or off</p>
            <p className="editUserInputLabel">Weekly reports</p>
            <p id="weeklyReportsText">Opt in this user for weekly reports sent via email?</p>
            <IonCheckbox
              style={{ marginBottom: "-2px", zIndex: "0" }}
              checked={appContext.userToEdit?.weeklyReports === 1}
              onIonChange={e =>
                appContext.setUserToEdit({ ...appContext.userToEdit!, weeklyReports: e.detail.checked ? 1 : 0 })
              }
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <IonButton
          disabled={shouldDisableButtton()}
          className="yellowBtn"
          id="saveChangesBtn"
          onClick={handleSaveChanges}
        >
          {savingChanges ? (
            <IonSpinner id="savingChangesSpinner" />
          ) : forNewUser ? (
            <p>Create user</p>
          ) : (
            <p>Save changes</p>
          )}
        </IonButton>
      </div>
      <IonAlert
        isOpen={showAlert}
        onDidDismiss={() => setShowAlert(false)}
        header={"Alert"}
        message={alertMessage}
        buttons={["OK"]}
      />
    </div>
  );
};

export default EditUserGrid;
